import * as React from 'react';
import Loader from '../Components/Loader';
import { AutomationRepository } from '../../MedicareProAPI/Repositories/Automation/AutomationRepository';
import { AutomationActionRepository } from '../../MedicareProAPI/Repositories/Automation/AutomationActionRepository';
import { AgencyDownlineRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyDownlineRepository';
import { AgencyRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyRepository';
import type { AutomationActionPayload } from '../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AutomationAction } from '../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AutomationActionForm } from '../Components/Automation/Action/AutomationActionForm';
import type {
  AutomationActionTriggerData,
  AutomationPayload,
} from '../../MedicareProAPI/Entities/Automation/Automation';
import {
  AUTOMATION_ACTION_TRIGGER_TYPE,
  AUTOMATION_TRIGGER_COMBINATION,
  AUTOMATION_TRIGGER_DATA_FIELD_TYPE,
  AUTOMATION_SHELL_TYPE,
  Automation,
} from '../../MedicareProAPI/Entities/Automation/Automation';
import { AUTOMATION_ACTION_TYPE } from '../../MedicareProAPI/Entities/Automation/AutomationAction';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import StyledModal from '../Components/StyledModal';
import { AutomationActionFormTrigger } from '../Components/Automation/AutomationActionFormTrigger';
import { AUTOMATION_TRIGGER_RECIPE_LABELS } from '../Language/en';
import { AutomationActionCard } from '../Components/Automation/Action/AutomationActionCard';
import { SIDETAB_POSITION, SideTab } from '../Components/SideTab';
import { AutomationForm } from '../Components/Automation/AutomationForm';
import { AutomationNavSingle } from '../Components/Automation/AutomationNavSingle';
import { AutomationActionCardList } from '../Components/Automation/Action/AutomationActionCardList';
import { AutomationCollection } from '../../MedicareProAPI/Collections/Automation/AutomationCollection';
import type { AutomationActionCollection } from '../../MedicareProAPI/Collections/Automation/AutomationActionCollection';
import AgentSettingsIntegrationPanel from './AgentSettingsIntegrationPanel';
import AgentSettingsVoIPPhonePanel from './AgentSettingsVoIPPhonePanel';
import TemplateManagerPanel from '../Panels/TemplateManagerPanel';
import { AutomationSelectorModal } from '../Components/Automation/AutomationSelectorModal';

import type { INTEGRATION_TYPE } from '../Constants/Integrations';
import { INTEGRATION_OPTIONS } from '../Constants/Integrations';
import { IntegrationManager } from '../Components/IntegrationManager';

export interface AutomationTriggerRecipe {
  value: AUTOMATION_TRIGGER_COMBINATION;
  entity: string;
  type: AUTOMATION_ACTION_TRIGGER_TYPE;
  field?: AUTOMATION_TRIGGER_DATA_FIELD_TYPE;
  availableFilters?: AUTOMATION_TRIGGER_DATA_FIELD_TYPE[];
}

export const AUTOMATION_ACTION_TRIGGER_RECIPES: AutomationTriggerRecipe[] = [
  {
    value: AUTOMATION_TRIGGER_COMBINATION.USER_LEAD_INSERT,
    entity: 'UserLead',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.INSERT,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_LEAD_STATUS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.USER_LEAD_STATUS_UPDATE,
    entity: 'UserLead',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.UPDATE,
    field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_LEAD_STATUS,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_POLICY_STATUS_INSERT,
    entity: 'IndPolicy',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.INSERT,
    field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_STATUS,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_WRITTEN_AS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.PLAN_TYPE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_COMPANY,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_POLICY_STATUS_UPDATE,
    entity: 'IndPolicy',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.UPDATE,
    field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_STATUS,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_WRITTEN_AS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.PLAN_TYPE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_COMPANY,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_POLICY_STATUS_UPSERT,
    entity: 'IndPolicy',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.UPSERT,
    field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_STATUS,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_WRITTEN_AS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.PLAN_TYPE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_COMPANY,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_POLICY_PREMIUM_INSERT,
    entity: 'IndPolicyAccVal',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.INSERT,
    //field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.ACCUMULATION_VALUE,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_WRITTEN_AS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.PLAN_TYPE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_POLICY_COMPANY,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_BIRTHDAY,
    entity: 'Ind',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.BIRTHDAY,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_AGE,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_BIRTHDAY_UPCOMING,
    entity: 'Ind',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.UPCOMING_BIRTHDAY,
    field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_BIRTHDAY_UPCOMING,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_BIRTHDAY_UPCOMING,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_AGE,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_STATUS_INSERT,
    entity: 'Ind',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.INSERT,
    field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_AGE,
    ],
  },
  {
    value: AUTOMATION_TRIGGER_COMBINATION.IND_STATUS_UPDATE,
    entity: 'Ind',
    type: AUTOMATION_ACTION_TRIGGER_TYPE.UPDATE,
    field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
    availableFilters: [
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_STATUS,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_SOURCE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_PREFERRED_LANGUAGE,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.USER_FLAG,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_GENDER,
      AUTOMATION_TRIGGER_DATA_FIELD_TYPE.IND_AGE,
    ],
  },
];

export default function AgentAutomationManagePanel(props) {
  const [userAgencyId, setUserAgencyId] = React.useState(null);
  const [userIsAgencyPrinciple, setUserIsAgencyPrinciple] = React.useState(null);
  const [agencyHasLoaAgents, setAgencyHasLoaAgents] = React.useState(false);
  const [automations, setAutomations] = React.useState(null);
  const [automationsActive, setAutomationsActive] = React.useState<Automation[]>(null); // Actions that are to be shown based on showShared
  const [selectedAutomation, setSelectedAutomation] = React.useState<Automation>(null);
  const [selectedAutomationActions, setSelectedAutomationActions] = React.useState(null);

  const [actionFormShow, setActionFormShow] = React.useState<boolean>(false);
  const [actionFormAction, setActionFormAction] = React.useState<AutomationAction>(null);
  const [actionFormActionIndex, setActionFormActionIndex] = React.useState<number>(null);

  const [automationFormTriggerEntity, setAutomationFormTriggerEntity] =
    React.useState<string>(null);
  const [automationFormTriggerType, setAutomationFormTriggerType] =
    React.useState<AUTOMATION_ACTION_TRIGGER_TYPE>(null);
  const [automationFormTriggerData, setAutomationFormTriggerData] =
    React.useState<AutomationActionTriggerData[]>();
  const [automationFormRecipe, setAutomationFormRecipe] =
    React.useState<AUTOMATION_TRIGGER_COMBINATION>(null);
  const [automationParentFormRecipe, setAutomationParentFormRecipe] =
    React.useState<AUTOMATION_TRIGGER_COMBINATION>(null);

  const [sharedAutomations, setSharedAutomations] = React.useState(null);

  const [automationFormId, setAutomationFormId] = React.useState(null);
  const [automationFormName, setAutomationFormName] = React.useState(null);
  const [automationFormParentAutomationId, setAutomationFormParentAutomationId] =
    React.useState(null);
  const [automationFormIsActive, setAutomationFormIsActive] = React.useState(false);
  const [automationFormIsShared, setAutomationFormIsShared] = React.useState(false);
  const [automationFormCanOverride, setAutomationFormCanOverride] = React.useState(false);

  const [showShared, setShowShared] = React.useState(false);

  const [showIntegrationSetup, setShowIntegrationSetup] = React.useState<boolean>(false);
  const [allowedIntegrationServices, setAllowedIntegrationServices] = React.useState<Array<string>>(
    []
  );
  const [allowedVoIPServices, setAllowedVoIPServices] = React.useState<Array<string>>([]);
  const [allowedSyncServices, setAllowedSyncServices] = React.useState<Array<string>>([]);

  const [showTemplateSetup, setShowTemplateSetup] = React.useState<boolean>(false);

  const [showAutomationTemplateSetup, setShowAutomationTemplateSetup] =
    React.useState<boolean>(false);

  const [refreshKey, setRefreshKey] = React.useState(0);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const automationRepository = new AutomationRepository();
  automationRepository.setAccessToken(props.userAccessToken);

  const agencyRepository = new AgencyRepository();
  agencyRepository.setAccessToken(props.userAccessToken);

  const automationActionRepository = new AutomationActionRepository();
  automationActionRepository.setAccessToken(props.userAccessToken);

  const agencyDownlineRepository = new AgencyDownlineRepository();
  agencyDownlineRepository.setAccessToken(props.userAccessToken);

  const statuses = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
  ];

  const noYes = [
    { value: false, label: 'No' },
    { value: true, label: 'Yes' },
  ];

  // Handle show action form
  const handleActionFormShow = (action: AutomationAction, index: number = null) => {
    setActionFormShow(true);
    setActionFormActionIndex(index);
    setActionFormAction(action);
  };

  // Handle hide action form
  const handleActionFormHide = () => {
    setActionFormShow(false);
    setActionFormAction(null);
  };

  // Update an automation, at a particular
  const updateSeletedAutomation = (newAutomation) => {
    const newAutomations = [...automations];
    const selectedAutomationIndex = newAutomations.indexOf(selectedAutomation);

    newAutomations[selectedAutomationIndex] = newAutomation;

    setAutomations(newAutomations);
    selectAutomation(newAutomation);
  };

  React.useEffect(() => {
    loadAutomations();
  }, []);

  React.useEffect(() => {
    if (automations) {
      setAutomationsActive(
        automations.filter((automation) => automation.getIsShared() === showShared)
      );
    } else {
      setAutomationsActive(null);
    }
  }, [automations, showShared]);

  React.useEffect(() => {
    // This is going to get all the data we need for an Agency principle
    if (props.agencyId) {
      // Can they make member actions
      webAPI.getUserIsAgencyMember(props.userId).then((isAgencyMember) => {
        setUserIsAgencyPrinciple(isAgencyMember);
      });

      setUserAgencyId(props.agencyId);
      automationRepository.getAgencyAutomations(props.agencyId).then((sharedAutomations) => {
        setSharedAutomations(sharedAutomations.getItems());
      });

      // See if they have LOA agents to enable the override field
      const options = {
        criteria: { agentStatus: 'LOA', agencyUplineId: props.agencyId },
        limit: 1,
      };
      agencyDownlineRepository
        .getAgencyDownlines(props.userId, props.agencyId, options)
        .then((downlines) => {
          downlines.getItems().length > 0 ? setAgencyHasLoaAgents(true) : '';
        });
    } else {
      // This is going to see if the user is part of an agency, if so we need shared templates
      agencyRepository.getUserAgency(props.userId).then((agency) => {
        if (agency.getAgencyId()) {
          setUserAgencyId(agency.getAgencyId());
          automationRepository
            .getAgencyAutomations(agency.getAgencyId())
            .then((sharedAutomations) => {
              setSharedAutomations(sharedAutomations.getItems());
            });
        }
      });
    }
  }, [showShared]);

  const clearSelectedAutomation = () => {
    setSelectedAutomation(null);
    setSelectedAutomationActions(null);
    setAutomationFormId(null);
    setAutomationFormRecipe(null);
    setAutomationParentFormRecipe(null);
    setAutomationFormTriggerData(null);
    setAutomationFormTriggerEntity(null);
    setAutomationFormTriggerType(null);
    setAutomationFormName(null);
    setAutomationFormIsActive(null);
    setAutomationFormIsShared(null);
    setAutomationFormCanOverride(null);
    setAutomationFormParentAutomationId(null);
  };

  const selectAutomationActions = (automationActions: AutomationActionCollection) => {
    let newSelectedAutomationActions = [...automationActions.getItems()];
    newSelectedAutomationActions.sort((a, b) => {
      return a.getStartDelayDays() > b.getStartDelayDays() ? 1 : -1;
    });

    newSelectedAutomationActions = newSelectedAutomationActions.filter((actionSingle) => {
      return actionSingle.getParentAction() === null;
    });

    setSelectedAutomationActions(newSelectedAutomationActions);
  };

  const selectAutomation = (automation: Automation, expand: boolean = null) => {
    setSelectedAutomation(automation);
    selectAutomationActions(automation.getAutomationActions());

    setAutomationFormId(automation.getId());
    setAutomationFormRecipe(automation.getRecipe());
    setAutomationFormTriggerData(automation.getTriggerData());
    setAutomationFormTriggerEntity(automation.getTriggerEntity());
    setAutomationFormTriggerType(automation.getTriggerType());
    setAutomationFormName(automation.getName());
    setAutomationFormIsActive(automation.getIsActive());
    setAutomationFormIsShared(automation.getIsShared());
    setAutomationFormCanOverride(automation.getCanOverride());
    setAutomationFormParentAutomationId(
      automation.getParentAutomation() ? automation.getParentAutomation().getId() : null
    );

    setShowShared(automation.getIsShared());

    automation.getParentAutomation()
      ? setAutomationParentFormRecipe(automation.getParentAutomation().getRecipe())
      : setAutomationParentFormRecipe(null);

    if (expand === false) {
      //setSelectedAutomationExpanded(false);
    }
  };

  /**
   * Get/refresh automations
   *
   * @returns {Promise<AutomationCollection>} Return target Automation
   */
  const loadAutomations = () => {
    return automationRepository.getAutomations(props.userId).then((automations) => {
      setAutomations(automations.getItems());

      if (automations.getTotal() > 0) {
        // If no automation is selected and user has automations then select top one, by default
        if (selectedAutomation === null) {
          selectAutomation(automations.getItem(0));
        }
        // If one is selected then refresh it's data
        else if (selectedAutomation !== null) {
          const currentAction = automations
            .getItems()
            .find((automationSingle) => automationSingle.getId() === selectedAutomation.getId());

          if (currentAction) {
            selectAutomation(currentAction);
          }
        }
      }

      return automations;
    });
  };

  /**
   * Delete automation
   *
   * @param {Automation} automation - Automation to delete
   * @returns {Promise<Automation>} Return target Automation
   */
  const deleteAutomation = (automation: Automation) => {
    // Promise returning an automation
    let deletionProcess: Promise<Automation>;

    // If this is a shared automation then flag it as deleted
    if (automation.getIsShared()) {
      deletionProcess = automationRepository.updateAutomation(props.userId, automation.getId(), {
        isDeleted: true,
      });
    }
    // Otherwise delete it permenantly
    else {
      deletionProcess = automationRepository.deleteAutomation(props.userId, automation.getId());
    }

    return deletionProcess.then(() => {
      const newAutomations = [...automations];
      const deletedIndex = newAutomations.indexOf(selectedAutomation);
      newAutomations.splice(deletedIndex, 1);
      setAutomations(newAutomations);

      if (newAutomations[0]) {
        selectAutomation(newAutomations[0]);
      } else {
        clearSelectedAutomation();
      }

      return automation;
    });
  };

  /**
   * Save automation
   *
   * @param {Automation} automation - Automation to save
   * @returns {Promise<Automation>} Return target Automation
   */
  const saveAutomation = (automation: Automation) => {
    let process;

    const payload: AutomationPayload = {
      name: automation.getName(),
      canOverride: automation.getCanOverride(),
      isActive: automation.getIsActive(),
      recipe: automation.getRecipe(),
      triggerEntity: automation.getTriggerEntity(),
      triggerType: automation.getTriggerType(),
      triggerData: automation.getTriggerData(),
    };

    if (automation.getId()) {
      process = automationRepository.updateAutomation(props.userId, automation.getId(), payload);
    } else {
      process = automationRepository.createAutomation(props.userId, payload);
    }

    return process.then((automation: Automation) => {
      selectAutomation(automation);

      loadAutomations().then((automations) => {
        setShowShared(automation.getIsShared());
      });

      return automation;
    });
  };

  /**
   * Share automation
   *
   * @param {Automation} automation - Automation to share
   * @returns {Promise<Automation>} Return target Automation
   */
  const shareAutomation = (automation: Automation) => {
    return automationRepository
      .updateAutomation(props.userId, automation.getId(), {
        isShared: true,
        parentAutomation: null,
      })
      .then((automation) => {
        updateSeletedAutomation(automation);

        setShowShared(true);

        return automation;
      });
  };

  /**
   * Change automation's parent automation
   *
   * @param {Automation} automation - Automation to update
   * @param {Automation} parentAutomation - Automation to assign as the parent autoamtion
   * @returns {Promise<Automation>} Return target Automation
   */
  const changeParentAutomation = (automation: Automation, parentAutomation: Automation) => {
    return new Promise<Automation>((resolve, reject) => {
      const parentAutomationId = parentAutomation ? parentAutomation.getId() : null;
      const payload: AutomationPayload = {
        parentAutomation: parentAutomationId,
      };

      automationRepository.updateAutomation(props.userId, automation.getId(), payload);

      const existingAutomationActions = automation.getAutomationActions().getItems();

      // Deletion promises
      const existingAutomationActionPromises = [];

      // Delete all actions
      existingAutomationActions.forEach((action) => {
        const deletedAction = automationActionRepository.deleteAutomationAction(
          props.userId,
          automation.getId(),
          action.getId()
        );
        existingAutomationActionPromises.push(deletedAction);
      });

      // Once all actions are deleted, create the new ones
      Promise.all(existingAutomationActionPromises).then((values) => {
        if (parentAutomationId) {
          const parentAutomation = sharedAutomations.find(
            (item) => item.getId() === parentAutomationId
          );
          const parentAutomationActions = parentAutomation.getAutomationActions().getItems();
          const newAutomationActionPromises = [];

          parentAutomationActions.forEach((action) => {
            const newAutomationActionPayload: AutomationActionPayload = {
              position: action.getPosition(),
              startDelayDays: action.getStartDelayDays(),
              startDelayTime: action.getStartDelayTime(),
              type: action.getType(),
            };

            const newAutomationAction = automationActionRepository.createAutomationAction(
              props.userId,
              automation.getId(),
              newAutomationActionPayload
            );

            newAutomationActionPromises.push(newAutomationAction);
          });

          // Once all new automation action have been added
          Promise.all(newAutomationActionPromises).then((values) => {
            // Pull uopdated automation with actions
            automationRepository
              .getAutomation(props.userId, automation.getId())
              .then((updatedAutomation) => {
                updateSeletedAutomation(updatedAutomation);

                resolve(updatedAutomation);
              });
          });
        } else {
          // Even if records are only deleted we update the automation
          automationRepository
            .getAutomation(props.userId, automation.getId())
            .then((updatedAutomation) => {
              updateSeletedAutomation(updatedAutomation);

              resolve(updatedAutomation);
            });
        }
      });
    });
  };

  /**
   * Delete automation action
   *
   * @param {Automation} automation - Automation this action belongs to
   * @param {AutomationAction} action - Automation action to delete
   * @returns {Promise<Automation>} Return target Automation
   */
  const deleteAutomationAction = (automation: Automation, action: AutomationAction) => {
    return automationActionRepository
      .deleteAutomationAction(props.userId, automation.getId(), action.getId())
      .then(() => {
        automationRepository
          .getAutomation(props.userId, automation.getId())
          .then((updatedAutomation) => {
            updateSeletedAutomation(updatedAutomation);
            handleActionFormHide();
          });
      });
  };

  /**
   * Save automation action
   *
   * @param {Automation} automation - Automation this action belongs to
   * @param {AutomationAction} action - Automation action to save
   * @returns {Promise<Automation>} Return target Automation
   */
  const saveAutomationAction = (automation: Automation, action: AutomationAction) => {
    // Create payload to save Automation Action data
    const payload: AutomationActionPayload = {
      type: action.getType(),
      startDelayTime: action.getStartDelayTime(),
      startDelayDays: action.getStartDelayDays(),
      triggerCriteria: action.getTriggerCriteria(),
      data: action.getData(),
    };

    // Promise to assign to update or insert method response
    let process;
    let newRecord = false;

    if (action.getId()) {
      process = automationActionRepository.updateAutomationAction(
        props.userId,
        automation.getId(),
        action.getId(),
        payload
      );
    } else {
      if (action.getParentAction()) {
        payload.parentAction = action.getParentAction().getId();
      }

      newRecord = true;
      process = automationActionRepository.createAutomationAction(
        props.userId,
        automation.getId(),
        payload
      );
    }

    // Handle API response for update or insert
    return process.then((automationAction) => {
      loadAutomations();

      return automationAction;
    });
  };

  const addAutomation = () => {
    const newAutomations = [...automations];
    const newAutomation = new Automation();
    newAutomations.push(newAutomation);

    setShowShared(false);
    setAutomations(newAutomations);
    selectAutomation(newAutomation);
  };

  const addAutomationAction = (automation: Automation, automationAction?: AutomationAction) => {
    const newAction = new AutomationAction();
    newAction.setAutomation(automation);

    if (automationAction) {
      newAction.setParentAction(automationAction);
    }

    handleActionFormShow(newAction);
  };

  const handleSetUpIntegration = (serviceType: INTEGRATION_TYPE) => {
    // setActionFormShow(false);
    setShowIntegrationSetup(true);
    setAllowedIntegrationServices([...INTEGRATION_OPTIONS[serviceType]]);
  };

  const handleSetUpIntegrationHide = () => {
    setShowIntegrationSetup(false);
    setAllowedIntegrationServices([]);
    setAllowedVoIPServices([]);
    setAllowedSyncServices([]);
    setRefreshKey((prev) => prev + 1);
  };

  const handleSetupTemplateHide = () => {
    setShowTemplateSetup(false);
    setRefreshKey((prev) => prev + 1);
  };

  const handleSetupTemplate = () => {
    handleSetUpIntegrationHide();
    setShowTemplateSetup(true);
  };

  const handleTemplateAutomationChosen = (type: AUTOMATION_SHELL_TYPE, automation?: Automation) => {
    setShowShared(false);
    setShowAutomationTemplateSetup(false);
    if (type === AUTOMATION_SHELL_TYPE.BLANK) {
      addAutomation();
    }

    if (type === AUTOMATION_SHELL_TYPE.STARTER) {
      const newAutomations = [...automations];

      newAutomations.push(automation);
      setAutomations(newAutomations);
      selectAutomation(automation);
    }
  };

  return (
    <>
      {/* Add automation modal */}
      <AutomationSelectorModal
        userId={props.userId}
        userAccessToken={props.userAccessToken}
        open={showAutomationTemplateSetup}
        onClose={() => setShowAutomationTemplateSetup(false)}
        onTemplateSelect={handleTemplateAutomationChosen}
      />

      {/* Add/Edit an Action */}
      <SideTab
        size="lg"
        position={SIDETAB_POSITION.RIGHT}
        title={actionFormAction && actionFormAction.getId() ? 'Edit Action' : 'Create Action'}
        show={actionFormShow}
        onClose={(e) => handleActionFormHide()}
      >
        {actionFormAction && (
          <AutomationActionForm
            userAccessToken={props.userAccessToken}
            userId={props.userId}
            shared={automationFormIsShared}
            automationHasParent={automationFormParentAutomationId}
            actionHasParent={actionFormAction.getParentAction() === null ? false : true}
            action={actionFormAction}
            automation={selectedAutomation}
            onDelete={(action) => deleteAutomationAction(selectedAutomation, action)}
            onSave={(action) => saveAutomationAction(selectedAutomation, action)}
            onSetupIntegrationService={handleSetUpIntegration}
            onSetupTemplate={handleSetupTemplate}
            refreshKey={refreshKey}
          />
        )}
      </SideTab>

      {/* Integration Manager */}
      <IntegrationManager
        userAccessToken={props.userAccessToken}
        userId={props.userId}
        showIntegrationSetup={showIntegrationSetup}
        onHide={handleSetUpIntegrationHide}
        allowedServices={allowedIntegrationServices}
        agencyId={props.agencyReference}
      />

      {/* Template Manager */}
      <SideTab
        size="lg"
        position={SIDETAB_POSITION.RIGHT}
        title={''}
        show={showTemplateSetup}
        onClose={(e) => handleSetupTemplateHide()}
      >
        <div className="alert alert-secondary p-2" style={{ fontSize: '10pt' }}>
          <i className="far fa-info-circle"></i> Templates can also be managed from the{' '}
          <a href="https://www.medicareproapp.com/agent/mybook/index">Reporting</a> page
        </div>
        {showTemplateSetup && (
          <TemplateManagerPanel
            userId={props.userId}
            userAccessToken={props.userAccessToken}
            agencyId={props.agencyId}
            uplineId={props.uplineId}
            absoluteUplineId={props.absoluteUplineId}
            renderedExternal={true}
          />
        )}
      </SideTab>

      <div className="card card-tall m-0 p-0">
        {/*  */}
        <div className="card-body m-0 p-0">
          {automationsActive && (
            <div className="row no-gutters">
              <div className="col-sm-3 automation-list pl-3 pr-3 pt-3 ">
                <div
                  className="btn border border-primary d-block mb-2 w-100"
                  onClick={() => setShowAutomationTemplateSetup(true)}
                >
                  <i className="fal fa-plus-circle text-primary "></i>
                  <span className="d-none d-sm-inline ml-2">New Automation</span>
                </div>

                {userIsAgencyPrinciple && automations && automations.length > 0 && (
                  <div>
                    <div className="clearfix">
                      <label className="switch float-left mr-1">
                        <input
                          type="checkbox"
                          data-status="off"
                          checked={showShared === true}
                          onChange={(e) => {
                            setShowShared(!showShared);
                          }}
                        />
                        <span className="slider round pt-1"></span>
                      </label>

                      <div className="small pt-0">Show Template Automations</div>
                    </div>
                  </div>
                )}
                {automationsActive &&
                  automationsActive.map((automation) => {
                    return (
                      <AutomationNavSingle
                        key={automation.getId()}
                        onClick={(automation) => selectAutomation(automation, false)}
                        automation={automation}
                        selected={
                          selectedAutomation && selectedAutomation.getId() === automation.getId()
                        }
                      />
                    );
                  })}
              </div>
              <div className="col-sm-9 automation-detail p-3">
                {
                  // No automation selected/available
                  !selectedAutomation && (
                    <div data-alert className="alert alert-info mx-3 mb-0">
                      <i className="fal fa-info-circle mr-2"></i>No automations to show. Click{' '}
                      <b>"New Automation"</b> to get started.
                    </div>
                  )
                }
                {
                  // Show selected automation
                  selectedAutomation && (
                    <AutomationForm
                      userIsAgencyPrinciple={userIsAgencyPrinciple}
                      automation={selectedAutomation}
                      automationActions={[]}
                      sharedAutomations={sharedAutomations}
                      onShare={(automation) => shareAutomation(automation)}
                      onDelete={(automation) => deleteAutomation(automation)}
                      onSave={(automation) => saveAutomation(automation)}
                      onChangeParentAutomation={(automation, parentAutomation) =>
                        changeParentAutomation(automation, parentAutomation)
                      }
                      userAccessToken={props.userAccessToken}
                      userId={props.userId}
                    />
                  )
                }
                {
                  // Show list of actions
                  selectedAutomationActions && (
                    <AutomationActionCardList
                      actions={selectedAutomationActions}
                      onClick={(updateAction) => handleActionFormShow(updateAction)}
                      onClickAddSubAction={(parentAction) =>
                        addAutomationAction(selectedAutomation, parentAction)
                      }
                    />
                  )
                }
                {
                  // Add new action to this automation
                  selectedAutomation &&
                    automationFormId &&
                    selectedAutomation.getTriggerType() &&
                    !automationFormParentAutomationId &&
                    automationFormRecipe &&
                    !automationFormIsShared && (
                      <button
                        type="button"
                        disabled={automationFormIsShared}
                        onClick={(e) => addAutomationAction(selectedAutomation)}
                        className="btn btn-sm btn-primary p-2 d-inline-block cp px-3"
                        style={{ border: 'none', borderRadius: '15px' }}
                      >
                        <i className="far fa-plus-circle mr-1" /> Add Action
                      </button>
                    )
                }
                {
                  // More actions can't be added as
                  (automationFormParentAutomationId || automationFormIsShared) && (
                    <div className="badge badge-pill table-warning text-dark p-1 px-1">
                      <i>Additional actions cannot be added to Templated Automation</i>
                    </div>
                  )
                }
              </div>
            </div>
          )}
          {!automations && <Loader size="large" className="mt-4 mb-4" />}
        </div>
      </div>
    </>
  );
}
